/**
 * SmartKick Scores — Capacitor push notifications wiring.
 *
 * The Scores app has no auth, so we identify devices by a stable
 * client-generated UUID stored in localStorage. The native Capacitor
 * @capacitor/push-notifications plugin handles permission prompts and
 * token issuance on iOS / Android; for the web build we silently fall
 * back to a noop so unsupported environments still work.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const DEVICE_ID_KEY = 'sk_scores_device_id_v1';

// ----- Device identity ------------------------------------------------------
const generateUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers — RFC4122 v4
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const getDeviceId = () => {
  if (typeof window === 'undefined') return null;
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = generateUuid();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
};

// ----- Deep-link routing ----------------------------------------------------
// Maps a notification's `data.deep_link` (e.g. "smartkick://predictions/value-bets")
// to an in-app path and navigates there when the user taps the notification.
const resolveDeepLink = (deepLink) => {
  if (!deepLink || typeof deepLink !== 'string') return null;
  const dl = deepLink.replace('smartkick://', '').replace(/^\/+/, '');
  if (dl.startsWith('predictions/value-bets')) return '/app/value-bets';
  if (dl.startsWith('predictions')) return '/app';
  if (dl.startsWith('news')) return '/news';
  if (dl.startsWith('scores')) return '/live-scores';
  return null;
};

const handleNotificationTap = (notification) => {
  try {
    const data = notification?.notification?.data || notification?.data || {};
    const path = resolveDeepLink(data.deep_link);
    if (path && typeof window !== 'undefined') {
      window.location.href = path;
    }
  } catch {
    /* ignore deep-link routing errors */
  }
};

// ----- Capacitor detection --------------------------------------------------
const isNative = () => {
  if (typeof window === 'undefined') return false;
  // Capacitor injects `Capacitor.isNativePlatform()` on native builds
  const cap = window.Capacitor;
  return Boolean(cap && typeof cap.isNativePlatform === 'function' && cap.isNativePlatform());
};

export const getNativePlatform = () => {
  if (typeof window === 'undefined') return 'web';
  const cap = window.Capacitor;
  if (cap && typeof cap.getPlatform === 'function') {
    const p = cap.getPlatform();
    if (p === 'ios' || p === 'android') return p;
  }
  return 'web';
};

// ----- Backend sync helpers -------------------------------------------------
const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const postJson = async (path, body) => {
  if (!API_BASE) return null;
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

export const registerScoresPushToken = async ({ deviceId, pushToken, platform }) =>
  postJson('/api/scores/push/register', { device_id: deviceId, push_token: pushToken, platform });

export const syncScoresPushPreferences = async (prefs) => {
  const deviceId = getDeviceId();
  if (!deviceId) return null;
  return postJson('/api/scores/push/preferences', {
    device_id: deviceId,
    platform: getNativePlatform(),
    ...prefs,
  });
};

// ----- Main hook ------------------------------------------------------------
/**
 * Registers the device with native APNs/FCM and posts the token to our
 * backend. On web this is a noop that simply yields { supported: false }.
 *
 * Returns: { supported, permission, token, error, request }
 *   - request(): trigger permission flow + registration on demand.
 */
export const usePushNotifications = () => {
  const [supported] = useState(isNative());
  const [permission, setPermission] = useState('default'); // 'default' | 'granted' | 'denied'
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const listenersRef = useRef([]);
  const listenersWiredRef = useRef(false);

  // Wire the registration listeners exactly once, then (re)issue the token.
  // Calling register() is idempotent and is Apple's recommended pattern to
  // run on every launch while authorized — it guarantees the freshest APNs
  // token reaches our backend even if a previous sync silently failed.
  const wireAndRegister = useCallback(async (PushNotifications) => {
    if (!listenersWiredRef.current) {
      const regHandle = await PushNotifications.addListener('registration', async (t) => {
        setToken(t.value);
        const deviceId = getDeviceId();
        if (deviceId) {
          await registerScoresPushToken({
            deviceId,
            pushToken: t.value,
            platform: getNativePlatform(),
          });
        }
      });
      const errHandle = await PushNotifications.addListener('registrationError', (e) => {
        setError(e?.error || 'registration_error');
      });
      // Route the user to the right tab when they tap a notification.
      const tapHandle = await PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (action) => handleNotificationTap(action),
      );
      listenersRef.current.push(regHandle, errHandle, tapHandle);
      listenersWiredRef.current = true;
    }
    await PushNotifications.register();
  }, []);

  const request = useCallback(async () => {
    if (!isNative()) {
      // Web platform — nothing to do.
      return { supported: false };
    }
    try {
      // Lazy import keeps the web bundle slim and avoids ESM resolution
      // errors when the native plugin is absent.
      const { PushNotifications } = await import('@capacitor/push-notifications');

      const perm = await PushNotifications.requestPermissions();
      setPermission(perm.receive);
      if (perm.receive !== 'granted') {
        return { supported: true, permission: perm.receive };
      }

      await wireAndRegister(PushNotifications);
      return { supported: true, permission: 'granted' };
    } catch (e) {
      setError(e?.message || String(e));
      return { supported: true, error: e?.message || String(e) };
    }
  }, [wireAndRegister]);

  // On every native launch: if the user already authorized notifications,
  // silently re-issue + re-sync the token so a stale/missing registration
  // self-heals without requiring a reinstall or another permission prompt.
  useEffect(() => {
    if (!isNative()) return;
    let cancelled = false;
    (async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const perm = await PushNotifications.checkPermissions();
        if (cancelled) return;
        setPermission(perm.receive);
        if (perm.receive === 'granted') {
          await wireAndRegister(PushNotifications);
        }
      } catch { /* plugin absent / web — ignore */ }
    })();
    return () => { cancelled = true; };
  }, [wireAndRegister]);

  // Cleanup listeners on unmount
  useEffect(() => {
    return () => {
      listenersRef.current.forEach((h) => {
        try { h?.remove?.(); } catch { /* noop */ }
      });
      listenersRef.current = [];
      listenersWiredRef.current = false;
    };
  }, []);

  return { supported, permission, token, error, request };
};
